import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils';

const productCategories = [
  {
    title: "Manhole Covers & Gratings",
    subtitle: "EN 124 Ductile Iron Solutions",
    description: "EN 124-compliant ductile iron manhole covers and frames with exceptional strength, anti-skid performance, and secure hinged designs.",
    features: ["Solid Top Covers", "Recessed Covers", "Channel Gratings", "Carriageway Covers"],
    image: "https://images.unsplash.com/photo-1590496793929-36417d3117de?w=600&q=80",
    color: "from-[#1a237e] to-[#3949ab]",
    page: "ManholeCoverProducts"
  },
  {
    title: "Ductile Pipes & Fittings",
    subtitle: "Complete Piping Solutions",
    description: "Comprehensive range of cast iron and ductile iron pipes, fittings, and gaskets for water, drainage, and industrial applications meeting industry requirements.",
    features: ["Spun Pipes (80-1200mm)", "Flanged Pipes", "Mechanical Fittings", "Rubber Gaskets"],
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80",
    color: "from-[#0d47a1] to-[#1976d2]",
    page: "PipesFittingsProducts"
  },
  {
    title: "Valves & Fasteners",
    subtitle: "Industrial Flow Control",
    description: "Complete range of valves from iron to alloy steel, including gate valves, butterfly valves, control valves, and high-quality fasteners.",
    features: ["Gate Valves", "Butterfly Valves", "Control Valves", "Stainless Steel Fasteners"],
    image: "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=600&q=80",
    color: "from-[#283593] to-[#5c6bc0]",
    page: "ValvesFastenersProducts"
  },
  {
    title: "Electrical Insulators",
    subtitle: "Power Transmission Components",
    description: "Porcelain and polymer insulators for electrical power transmission, including suspension disc, pin, and post insulators.",
    features: ["Porcelain Insulators", "Polymer Insulators", "Post Insulators", "Traction Insulators",],
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&q=80",
    color: "from-[#1a237e] to-[#303f9f]",
    page: "InsulatorsProducts"
  }
];

export default function ProductsSection() {
  return (
    <section className="py-14 bg-gray-100" id="products">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#d4a106] font-medium tracking-wider uppercase text-sm">Our Products</span>
          <h2 className="text-4xl md:text-5xl font-light text-[#1a237e] mt-4 mb-6">
            Engineering <span className="font-medium">Excellence</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            A multi-product marketing organisation with deep linkages to manufacturing partners, 
            providing an extensive range of engineering goods at unmatched value.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {productCategories.map((product, index) => (
            <motion.div
              key={product.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
            >
              <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" 
                   style={{ background: `linear-gradient(135deg, rgba(26,35,126,0.03), rgba(249,215,28,0.03))` }} />
              
              <div className="relative z-10 p-8">
                <div className="flex items-start gap-6">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${product.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <div className="w-10 h-10 bg-white/20 rounded-lg" />
                  </div>
                  <div className="flex-1">
                    <span className="text-[#d4a106] text-xs font-medium tracking-wider uppercase">{product.subtitle}</span>
                    <h3 className="text-2xl font-semibold text-[#1a237e] mt-1 mb-3">{product.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">{product.description}</p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature) => (
                      <span 
                        key={feature}
                        className="px-3 py-1.5 bg-gray-50 text-gray-600 text-xs rounded-full border border-gray-100"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <Link to={createPageUrl(product.page)} className="mt-6 flex items-center text-[#1a237e] font-medium text-sm group-hover:text-[#d4a106] transition-colors">
                  <span>Learn More</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Product Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-[#1a237e] rounded-3xl p-8 md:p-12 text-center"
        >
          <h3 className="text-2xl md:text-3xl font-light text-white mb-4">
            All Products Can Be <span className="font-medium text-[#f9d71c]">Customized</span>
          </h3>
          <p className="text-white/70 max-w-2xl mx-auto">
            Any of our products can be customized in size and material as per customer requirements. 
            Contact us to discuss your specific engineering needs.
          </p>
        </motion.div>
      </div>
    </section>
  );
}